import { c as _c } from "react/compiler-runtime";
import SettingConfig from '@module/config/setting';
import useGetLoginInfoQuery from '@module/helpers/auth/useGetLoginInfoQuery';
import { aspCond } from '@module/helpers/conditions/condition';
import { useModalAdapter } from '@module/helpers/modal/adapter';
import type { LivePlayErrorHandlerParams, UseLivePlayErrorHook } from '@module/helpers/play/playInfoErrorTypes';
import { useFetchItemUseMutation } from '@module/queries/item/itemQueryHooks';
import { useFetchLivePlayMutationStore } from '@module/stores/useFetchLivePlayMutationStore';
import useOldPlayerStore from '@module/stores/useOldPlayerStore';
import catchAllErrors from '@module/utils/catchAllErrors';
import { useQueryClient } from '@tanstack/react-query';
import { usePathname, useRouter } from 'next/navigation';
const useLivePlayErrorHandler: UseLivePlayErrorHook = () => {
  const $ = _c(22);
  const pathname = usePathname();
  const router = useRouter();
  const modalAdapter = useModalAdapter();
  const {
    isLogin
  } = useGetLoginInfoQuery();
  const queryClient = useQueryClient();
  const ivsPlayer = useOldPlayerStore(_temp);
  let t0;
  if ($[0] !== pathname) {
    t0 = pathname.includes("/setting/broadcast") || pathname.includes("/broadcast/setting");
    $[0] = pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const isBroadcastSettingPage = t0;
  let t1;
  if ($[2] !== pathname) {
    t1 = pathname.includes("/play/");
    $[2] = pathname;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const isPlayPage = t1;
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      onError: _temp2
    };
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    mutateAsync: mutateUseItem
  } = useFetchItemUseMutation(t2);
  let t3;
  if ($[5] !== isPlayPage || $[6] !== modalAdapter || $[7] !== router) {
    t3 = () => {
      modalAdapter.clear();
      if (!isPlayPage || aspCond.if("iframe-asp")) {
        return;
      }
      router.replace("/live");
    };
    $[5] = isPlayPage;
    $[6] = modalAdapter;
    $[7] = router;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const clearAndGoLive = t3;
  let t4;
  if ($[9] !== modalAdapter) {
    t4 = async description => {
      while (true) {
        const token = await modalAdapter.showRecaptcha({
          description
        });
        if (token === null) {
          return null;
        }
        if (!token.length) {
          await modalAdapter.showConfirm({
            description: "\uC790\uB3D9\uC811\uC18D\uBC29\uC9C0 \uCCB4\uD06C\uBC15\uC2A4\uB97C \uD655\uC778\uD574\uC8FC\uC138\uC694"
          });
          continue;
        }
        return token;
      }
    };
    $[9] = modalAdapter;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const requestRecaptchaToken = t4;
  let t5;
  if ($[11] !== clearAndGoLive || $[12] !== isBroadcastSettingPage || $[13] !== isLogin || $[14] !== isPlayPage || $[15] !== ivsPlayer || $[16] !== modalAdapter || $[17] !== mutateUseItem || $[18] !== queryClient || $[19] !== requestRecaptchaToken || $[20] !== router) {
    const handleLivePlayError = async params => {
      const {
        userId,
        err: err_0,
        fetchLivePlayParams,
        mutateAsync
      } = params;
      const errorCode = err_0.serverResponse.errorData?.code;
      if (!errorCode) {
        await modalAdapter.showConfirm({
          description: err_0.message
        });
        clearAndGoLive();
        return;
      }
      bb36: switch (errorCode) {
        case "needLogin":
          {
            if (!isLogin) {
              ivsPlayer?.dispose();
              await modalAdapter.showConfirm({
                description: "\uB85C\uADF8\uC778 \uD6C4\uC5D0 \uC774\uC6A9\uC774 \uAC00\uB2A5\uD55C \uBC29\uC1A1\uC785\uB2C8\uB2E4."
              });
              clearAndGoLive();
              break bb36;
            }
            await modalAdapter.showConfirm({
              description: err_0.message
            });
            clearAndGoLive();
            break bb36;
          }
        case "recaptcha":
          {
            const token_0 = await requestRecaptchaToken(err_0.serverResponse.message);
            if (token_0) {
              const store = useFetchLivePlayMutationStore.getState();
              const variables = store.getVariables();
              if (variables) {
                variables.recaptcha = token_0;
                mutateAsync?.(variables);
              }
              modalAdapter.clear();
            } else {
              clearAndGoLive();
            }
            break bb36;
          }
        case "needPw":
          {
            const {
              status: status_0,
              password: password_0
            } = await modalAdapter.showPasswordEntry();
            if (status_0 === "cancel") {
              if (isPlayPage && !useFetchLivePlayMutationStore.getState().data) {
                clearAndGoLive();
              }
              break bb36;
            }
            if (status_0 === "submit" && password_0) {
              mutateAsync?.({
                action: "watch",
                userId,
                password: password_0
              });
            }
            break bb36;
          }
        case "wrongPw":
          {
            await modalAdapter.showConfirm({
              description: err_0.message
            });
            const {
              status,
              password
            } = await modalAdapter.showPasswordEntry();
            if (status === "cancel") {
              if (isPlayPage && !useFetchLivePlayMutationStore.getState().data) {
                clearAndGoLive();
              }
              break bb36;
            }
            if (status === "submit" && password) {
              mutateAsync?.({
                action: "watch",
                userId,
                password
              });
            }
            break bb36;
          }
        case "castEnd":
          {
            if (isBroadcastSettingPage) {
              return;
            }
            await modalAdapter.showConfirm({
              description: "\uC885\uB8CC\uB41C \uBC29\uC1A1\uC785\uB2C8\uB2E4."
            });
            const currentPlayInfo = useFetchLivePlayMutationStore.getState().data;
            if (isPlayPage && !currentPlayInfo) {
              router.replace(`/channel/${userId}/notice`);
            }
            break bb36;
          }
        case "blockWatch":
          {
            await modalAdapter.showConfirm({
              description: err_0.message
            });
            clearAndGoLive();
            break bb36;
          }
        case "needCoin":
        case "needFan":
          {
            if (!isLogin) {
              ivsPlayer?.dispose();
              await modalAdapter.showConfirm({
                description: "\uB85C\uADF8\uC778 \uD6C4\uC5D0 \uC774\uC6A9\uC774 \uAC00\uB2A5\uD55C \uBC29\uC1A1\uC785\uB2C8\uB2E4."
              });
              clearAndGoLive();
              break bb36;
            }
            const confirmed_3 = await modalAdapter.showConfirmCancel({
              description: err_0.message
            });
            if (confirmed_3) {
              mutateAsync?.({
                ...fetchLivePlayParams,
                action: "spon",
                heart: err_0.serverResponse.errorData?.info?.needCoin
              });
            } else {
              clearAndGoLive();
            }
            break bb36;
          }
        case "needCoinPurchase":
          {
            if (!isLogin) {
              ivsPlayer?.dispose();
              await modalAdapter.showConfirm({
                description: "\uB85C\uADF8\uC778 \uD6C4\uC5D0 \uC774\uC6A9\uC774 \uAC00\uB2A5\uD55C \uBC29\uC1A1\uC785\uB2C8\uB2E4."
              });
              clearAndGoLive();
              break bb36;
            }
            const handleMoveToStore = async () => {
              if (aspCond.if("asp")) {
                return SettingConfig.aspUtils?.goCharge();
              }
              if (modalAdapter.navigateToStore) {
                return modalAdapter.navigateToStore({
                  tab: "heart"
                });
              }
              if (modalAdapter.showStore) {
                return await modalAdapter.showStore({
                  tab: "heart"
                });
              }
            };
            const confirmed_2 = await modalAdapter.showConfirmCancel({
              description: err_0.message,
              confirmText: "\uCDA9\uC804\uD558\uAE30",
              cancelText: "\uB2EB\uAE30"
            });
            if (!confirmed_2) {
              clearAndGoLive();
              break bb36;
            }
            modalAdapter.clear();
            await handleMoveToStore();
            break bb36;
          }
        case "needUnlimitItem":
          {
            if (!isLogin) {
              ivsPlayer?.dispose();
              await modalAdapter.showConfirm({
                description: "\uB85C\uADF8\uC778 \uD6C4\uC5D0 \uC774\uC6A9\uC774 \uAC00\uB2A5\uD55C \uBC29\uC1A1\uC785\uB2C8\uB2E4."
              });
              clearAndGoLive();
              break bb36;
            }
            const confirmed_1 = await modalAdapter.showConfirmCancel({
              description: err_0.message,
              confirmText: "\uD655\uC778",
              cancelText: "\uCDE8\uC18C"
            });
            if (!confirmed_1) {
              clearAndGoLive();
              break bb36;
            }
            modalAdapter.clear();
            if (aspCond.if("iframe-asp")) {
              SettingConfig.aspUtils?.goCharge("item");
              break bb36;
            }
            if (modalAdapter.navigateToStore) {
              modalAdapter.navigateToStore({
                tab: "item"
              });
              break bb36;
            }
            if (modalAdapter.showStore) {
              await modalAdapter.showStore({
                tab: "item"
              });
              break bb36;
            }
            break bb36;
          }
        case "needAdult":
          {
            if (!isLogin) {
              ivsPlayer?.dispose();
              await modalAdapter.showConfirm({
                description: "\uB85C\uADF8\uC778 \uD6C4\uC5D0 \uC774\uC6A9\uC774 \uAC00\uB2A5\uD55C \uBC29\uC1A1\uC785\uB2C8\uB2E4."
              });
              clearAndGoLive();
              break bb36;
            }
            const confirmed_0 = await modalAdapter.showConfirmCancel({
              description: "\uBCF8\uC778\uC778\uC99D \uD6C4 \uC2DC\uCCAD\uC774 \uAC00\uB2A5\uD569\uB2C8\uB2E4. \uC9C0\uAE08 \uBCF8\uC778\uC778\uC99D \uD558\uC2DC\uACA0\uC2B5\uB2C8\uAE4C?"
            });
            modalAdapter.clear();
            if (!confirmed_0) {
              router.back();
              break bb36;
            }
            if (modalAdapter.navigateToAuth) {
              modalAdapter.navigateToAuth();
              break bb36;
            }
            const targetPath = aspCond.if("iframe-asp") ? "/asp/play" : "/live";
            router.push(targetPath);
            await modalAdapter.showIdentityVerification();
            break bb36;
          }
        case "useUnlimitItem":
          {
            if (!isLogin) {
              ivsPlayer?.dispose();
              await modalAdapter.showConfirm({
                description: "\uB85C\uADF8\uC778 \uD6C4\uC5D0 \uC774\uC6A9\uC774 \uAC00\uB2A5\uD55C \uBC29\uC1A1\uC785\uB2C8\uB2E4."
              });
              clearAndGoLive();
              break bb36;
            }
            const confirmed = await modalAdapter.showConfirmCancel({
              description: err_0.message
            });
            if (confirmed) {
              await mutateUseItem({
                itemType: "entUnlimit",
                use: "Y"
              });
              queryClient.invalidateQueries({
                queryKey: ["live"],
                exact: false
              });
              mutateAsync?.(fetchLivePlayParams);
              modalAdapter.clear();
            } else {
              clearAndGoLive();
            }
            break bb36;
          }
        case "selfWatchMedia":
          {
            if (!isLogin) {
              await modalAdapter.showConfirm({
                description: err_0.message
              });
              clearAndGoLive();
              break bb36;
            }
            modalAdapter.clear();
            router.push(aspCond.if("iframe-asp") ? "/asp/play" : "/live");
            break bb36;
          }
        case "block":
          {
            if (!isLogin) {
              await modalAdapter.showConfirm({
                description: err_0.message
              });
              clearAndGoLive();
              break bb36;
            }
            await modalAdapter.showConfirm({
              description: err_0.message
            });
            if (aspCond.if("iframe-asp")) {
              router.push("/asp/play");
              modalAdapter.clear();
            } else {
              clearAndGoLive();
            }
            break bb36;
          }
        default:
          {
            await modalAdapter.showConfirm({
              description: err_0.message
            });
            clearAndGoLive();
          }
      }
    };
    t5 = {
      handleLivePlayError
    };
    $[11] = clearAndGoLive;
    $[12] = isBroadcastSettingPage;
    $[13] = isLogin;
    $[14] = isPlayPage;
    $[15] = ivsPlayer;
    $[16] = modalAdapter;
    $[17] = mutateUseItem;
    $[18] = queryClient;
    $[19] = requestRecaptchaToken;
    $[20] = router;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  return t5;
};
export default useLivePlayErrorHandler;
function _temp(state) {
  return state.player;
}
function _temp2(err) {
  return catchAllErrors(err);
}